import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ExternalLink, LayoutDashboard, LogOut, Mail, Menu, Newspaper, Smartphone, UserX, X } from "lucide-react";
import { useAdminAuth } from "./AdminAuth";

const NAV = [
    { to: "/", label: "Πίνακας", icon: LayoutDashboard, testId: "admin-nav-dashboard", end: true },
    { to: "/articles", label: "Νέα / Άρθρα", icon: Newspaper, testId: "admin-nav-articles" },
    { to: "/messages", label: "Μηνύματα", icon: Mail, testId: "admin-nav-messages" },
    { to: "/deletion-requests", label: "Αιτήματα διαγραφής", icon: UserX, testId: "admin-nav-deletions" },
    { to: "/android", label: "Android Waitlist", icon: Smartphone, testId: "admin-nav-android" },
];

const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${
        isActive ? "bg-[#22A6C0]/15 text-[#4CC9DC]" : "text-[#A3B3B8] hover:bg-white/5 hover:text-[#F2F5F6]"
    }`;

export const AdminLayout = ({ children }) => {
    const { user, logout } = useAdminAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const doLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const navContent = (
        <>
            <div className="flex items-center gap-3 px-4 pb-8">
                <img src="/brand/app-icon-256.webp" alt="TheraOffice" className="h-10 w-10 rounded-xl" />
                <div>
                    <p className="text-sm font-bold text-[#F2F5F6]">TheraOffice</p>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#697B80]">Admin Panel</p>
                </div>
            </div>
            <nav className="flex-1 space-y-1">
                {NAV.map(({ to, label, icon: Icon, testId, end }) => (
                    <NavLink key={to} to={to} end={end} className={linkCls} onClick={() => setOpen(false)} data-testid={testId}>
                        <Icon size={17} />
                        {label}
                    </NavLink>
                ))}
            </nav>
            <div className="space-y-1 border-t border-white/5 pt-4">
                <a href="/" className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-[#A3B3B8] transition-colors hover:bg-white/5 hover:text-[#F2F5F6]" data-testid="admin-view-site">
                    <ExternalLink size={17} />
                    Προβολή site
                </a>
                <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-[#A3B3B8] transition-colors hover:bg-red-400/10 hover:text-red-300" data-testid="admin-logout-button">
                    <LogOut size={17} />
                    Αποσύνδεση
                </button>
                {user && <p className="truncate px-4 pt-2 text-[11px] text-[#697B80]">{user.email}</p>}
            </div>
        </>
    );

    return (
        <div className="min-h-screen bg-[#052536]" data-testid="admin-layout">
            {/* Desktop sidebar */}
            <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-white/5 bg-[#093448]/60 p-4 lg:flex">
                {navContent}
            </aside>

            {/* Mobile top bar */}
            <header className="fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-white/5 bg-[#093448]/95 px-4 py-3 backdrop-blur-md lg:hidden">
                <div className="flex items-center gap-2.5">
                    <img src="/brand/app-icon-256.webp" alt="" className="h-8 w-8 rounded-lg" />
                    <span className="text-sm font-bold text-[#F2F5F6]">TheraOffice Admin</span>
                </div>
                <button onClick={() => setOpen(true)} className="rounded-lg border border-white/10 p-2 text-[#A3B3B8]" aria-label="Μενού" data-testid="admin-menu-button">
                    <Menu size={18} />
                </button>
            </header>

            {/* Mobile drawer */}
            {open && (
                <div className="fixed inset-0 z-40 lg:hidden" data-testid="admin-mobile-drawer">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
                    <aside className="absolute inset-y-0 left-0 flex w-64 flex-col bg-[#093448] p-4">
                        <button onClick={() => setOpen(false)} className="mb-4 ml-auto rounded-lg border border-white/10 p-2 text-[#A3B3B8]" aria-label="Κλείσιμο" data-testid="admin-menu-close">
                            <X size={18} />
                        </button>
                        {navContent}
                    </aside>
                </div>
            )}

            <main className="px-4 pb-16 pt-20 md:px-8 lg:ml-60 lg:pt-10">{children}</main>
        </div>
    );
};
